import React from 'react'

import { XStack } from 'tamagui'

import { Text } from '../Text'
import { Group } from '../Group'
import {
  SUPPORTED_LOCALES,
  LOCALE_LABELS,
  type SupportedLocale,
} from '@data/i18n/locales'

import { StyledIconWrapper, StyledIcon } from './DynamicIslandProfileExpand'

/**
 * Renders the locale toggle row (globe icon + current locale label).
 * Used as a Group.Item child — the expanded locale options are rendered
 * as separate Group.Items by the parent via <LocaleOptions />.
 */
export const LocaleToggle = ({
  currentLocale,
  isHovered,
  expanded,
  onHoverStart,
  onHoverEnd,
  onPress,
}: {
  currentLocale: SupportedLocale
  isHovered: boolean
  expanded: boolean
  onHoverStart: () => void
  onHoverEnd: () => void
  onPress: () => void
}) => (
  <XStack
    gap='$3'
    alignItems='center'
    padding='$1'
    paddingRight='$5'
    cursor='pointer'
    onMouseEnter={onHoverStart}
    onMouseLeave={onHoverEnd}
    onPress={onPress}
  >
    <StyledIconWrapper
      backgroundColor={
        isHovered || expanded ? '$backgroundPress' : '$backgroundStrong'
      }
    >
      <StyledIcon name='globe' />
    </StyledIconWrapper>

    <Text minWidth={78}>{LOCALE_LABELS[currentLocale]}</Text>
  </XStack>
)

/**
 * Renders expanded locale option rows as Group.Items.
 * Each locale gets its own Group.Item so it participates in the
 * parent Group's layout and highlighting.
 */
export const LocaleOptions = ({
  currentLocale,
  startIndex,
  onSelect,
}: {
  currentLocale: SupportedLocale
  startIndex: number
  onSelect: (locale: SupportedLocale) => void
}) => (
  <>
    {SUPPORTED_LOCALES.map((loc, i) => (
      <Group.Item key={loc} index={startIndex + i}>
        <XStack
          paddingLeft='$9'
          paddingVertical='$1'
          paddingRight='$5'
          cursor='pointer'
          onPress={() => onSelect(loc)}
        >
          <Text
            size='$4'
            fontWeight={loc === currentLocale ? '600' : '400'}
            color={loc === currentLocale ? '$color' : '$colorSubtle'}
          >
            {LOCALE_LABELS[loc]}
          </Text>
        </XStack>
      </Group.Item>
    ))}
  </>
)
